'use client';

import React, { useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';

const useScrollReveal = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    const elements = document.querySelectorAll('.fade-in-up');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

interface UniversityCardProps {
  name: string;
  city: string;
  type: string;
  founded: string;
  ranking: string;
  programmes: string[];
  tuition: string;
  postStudy: string;
  about: string;
}

const UniversityCard = ({ name, city, type, founded, ranking, programmes, tuition, postStudy, about }: UniversityCardProps) => (
  <div
    className="fade-in-up hover-lift bg-white rounded-2xl overflow-hidden border"
    style={{ borderColor: '#e0d8c8' }}
  >
    <div className="p-8">
      <div className="flex flex-wrap gap-2 mb-4">
        <span className="inline-block px-3 py-1 rounded-full text-xs font-body font-semibold uppercase tracking-wider" style={{ backgroundColor: '#e8f4f9', color: '#2A7FA5' }}>
          {type}
        </span>
        <span className="inline-block px-3 py-1 rounded-full text-xs font-body font-semibold uppercase tracking-wider" style={{ backgroundColor: '#F5EDD6', color: '#3d5566' }}>
          {city}
        </span>
      </div>
      <h3 className="font-headline font-bold mb-2" style={{ fontSize: '1.4rem', color: '#1a2e3b' }}>
        {name}
      </h3>
      <div className="flex flex-wrap gap-4 mb-4 text-sm font-body" style={{ color: '#5a6e7a' }}>
        <span>Founded {founded}</span>
        <span>·</span>
        <span>{ranking}</span>
      </div>
      <div className="flex flex-wrap gap-2 mb-5">
        {programmes.map((p, i) => (
          <span key={i} className="inline-block px-2.5 py-1 rounded text-xs font-body font-medium" style={{ backgroundColor: '#fdfaf5', color: '#3d5566', border: '1px solid #e0d8c8' }}>
            {p}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap gap-6 mb-5 text-sm font-body font-medium" style={{ color: '#1a2e3b' }}>
        <span>💰 {tuition}</span>
        <span>🎓 Post-study: {postStudy}</span>
      </div>
      <p className="font-body text-sm leading-relaxed" style={{ color: '#5a6e7a' }}>
        {about}
      </p>
    </div>
  </div>
);

export default function UniversitiesSpainPage() {
  useScrollReveal();

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative h-[60vh] min-h-[480px] flex items-end overflow-hidden">
        <div className="absolute inset-0 z-0">
          <Image
            src="/assets/images/florian-wehde-WBGjg0DsO_g-unsplash-1779197309792.jpg"
            alt="Madrid skyline with the iconic Gran Via and historic architecture at dusk"
            fill
            className="object-cover object-center"
            priority
            quality={90}
          />
          <div className="absolute inset-0 city-overlay" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 pb-16 pt-32">
          <p className="eyebrow text-white/80 mb-4">Universities</p>
          <h1 className="font-headline font-extrabold text-white" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            Study in Spain
          </h1>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 fade-in-up">
          <span className="accent-bar" />
          <p className="font-body leading-relaxed" style={{ fontSize: '1.2rem', color: '#3d5566' }}>
            Spain is home to some of Europe's most exciting universities — globally ranked degrees, English-taught programmes, and a 2-year post-study work visa that opens the door to a European career. Our consultants work with five leading institutions across Madrid and Barcelona to help you find your perfect fit.
          </p>
        </div>
      </section>

      {/* MADRID SECTION */}
      <section className="pb-24">
        {/* Madrid City Header */}
        <div className="relative h-64 mb-16 overflow-hidden">
          <Image
            src="/assets/images/florian-wehde-WBGjg0DsO_g-unsplash-1779197309792.jpg"
            alt="Madrid Gran Via boulevard with vibrant city life and illuminated buildings"
            fill
            className="object-cover object-center"
          />
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(26,46,59,0.65)' }} />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-6xl mx-auto px-6">
              <h2 className="font-headline font-bold text-white" style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)' }}>
                Madrid
              </h2>
              <p className="font-body text-white/80 text-lg mt-2">Capital of Spain · World-Class Universities</p>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-8">
          <UniversityCard
            name="Universidad Complutense de Madrid (UCM)"
            city="Madrid"
            type="Public"
            founded="1293"
            ranking="QS: Top 200 Europe"
            programmes={['Law', 'Medicine', 'Business', 'Humanities', 'Engineering']}
            tuition="€6,000–14,000/year"
            postStudy="2 years"
            about="The largest university in the Spanish-speaking world with 730+ years of history. Our consultants recommend UCM for students seeking an affordable, internationally recognised EU postgraduate degree in Law, Business, or Life Sciences. Erasmus+ and MEC scholarships available."
          />
          <UniversityCard
            name="Universidad Autónoma de Madrid (UAM)"
            city="Madrid"
            type="Public Research"
            founded="1968"
            ranking="QS: Top 400 Global"
            programmes={['AI & Computing', 'Economics', 'Molecular Sciences', 'Psychology', 'Law']}
            tuition="€7,000–15,000/year"
            postStudy="2 years"
            about="Spain's most research-intensive public university. Our consultants particularly recommend UAM for STEM, data science, and economics students who want academic depth, EU research access, and Erasmus Mundus joint degree opportunities."
          />
          <UniversityCard
            name="IE University"
            city="Madrid & Segovia"
            type="Private"
            founded="1973"
            ranking="FT MBA: Top 10 Global"
            programmes={['MBA', 'Finance & FinTech', 'Law & Policy', 'Data & AI', 'Architecture']}
            tuition="€22,000–38,000/year"
            postStudy="2 years"
            about="Spain's most internationally renowned private institution. Our consultants recommend IE for students targeting careers in consulting, finance, and law, with strong GCC alumni networks in Dubai, Singapore, and London. Merit and need-based scholarships available. 100% English. Triple Accredited: AACSB, EQUIS, AMBA."
          />
          <UniversityCard
            name="Comillas Pontificia Universidad"
            city="Madrid"
            type="Private Jesuit"
            founded="1890"
            ranking="QS: Top 600 Global"
            programmes={['Business & Analytics', 'Engineering & Technology', 'Law & Social Sciences']}
            tuition="€8,000–16,000/year"
            postStudy="2 years"
            about="A 134-year Jesuit institution with a 96% graduate employment rate within 6 months. Our consultants recommend Comillas for students seeking dual degrees in Business and Law or Engineering and Technology, with direct employer pipelines into Deloitte, BBVA, and Accenture."
          />
          <UniversityCard
            name="European University of Madrid (Universidad Europea)"
            city="Alcobendas, Madrid"
            type="Private"
            founded="1995"
            ranking="QS: 5-Star, Top 409 Europe"
            programmes={['Business & MBA', 'Sports Management', 'Health Sciences', 'Engineering', 'Law']}
            tuition="€9,000–18,000/year"
            postStudy="2 years"
            about="Spain's largest private university with 35,000+ students and a globally unique Real Madrid Graduate School partnership. Our consultants recommend Universidad Europea for students targeting Business, Sports Management, or Health Sciences in a dynamic corporate environment. HyFlex learning format."
          />
        </div>
      </section>

      {/* BARCELONA SECTION */}
      <section className="pb-24" style={{ backgroundColor: '#fdfaf5' }}>
        {/* Barcelona City Header */}
        <div className="relative h-64 mb-16 overflow-hidden">
          <Image
            src="/assets/images/benjamin-gremler-q_TzfAt4NQ8-unsplash-1779197319592.jpg"
            alt="Barcelona coastline with the Mediterranean sea, Barceloneta beach and city skyline"
            fill
            className="object-cover object-center"
          />
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(26,46,59,0.65)' }} />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-6xl mx-auto px-6">
              <h2 className="font-headline font-bold text-white" style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)' }}>
                Barcelona
              </h2>
              <p className="font-body text-white/80 text-lg mt-2">Cosmopolitan Capital · Mediterranean Lifestyle</p>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6">
          <UniversityCard
            name="ESADE Business & Law School"
            city="Barcelona"
            type="Private"
            founded="1958"
            ranking="FT European MBA: Top 10 · Forbes MBA: Top 5"
            programmes={['MBA', 'Executive MBA', 'Finance', 'Law & Compliance', 'Entrepreneurship']}
            tuition="€28,000–42,000/year"
            postStudy="2 years"
            about="Consistently ranked among Europe's top five business schools. Our consultants recommend ESADE for ambitious students targeting private equity, international law, and consulting careers, with a strong alumni base across the UAE, Saudi Arabia, and Kuwait. 100% English. Triple Accredited: AACSB, EQUIS, AMBA."
          />
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" style={{ backgroundColor: '#2A7FA5' }}>
        <div className="max-w-3xl mx-auto px-6 text-center fade-in-up">
          <h2 className="font-headline font-bold text-white mb-4" style={{ fontSize: 'clamp(2rem, 4vw, 2.8rem)' }}>
            Find Your Perfect University in Spain
          </h2>
          <p className="font-body text-white/80 mb-8" style={{ fontSize: '1.1rem' }}>
            Our consultants will match you with the right institution based on your profile, goals, and budget.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center justify-center px-10 py-4 font-body font-semibold rounded-lg transition-all duration-300 hover:-translate-y-1"
            style={{ backgroundColor: '#F5EDD6', color: '#2A7FA5', fontSize: '1.05rem' }}
          >
            Book a Free Consultation
          </Link>
        </div>
      </section>
    </div>
  );
}
